import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router";
import {
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  Pencil,
  RefreshCw,
} from "lucide-react";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import { ApiError } from "@shared-core/data/api/errors";
import type {
  ReplenishmentPolicyEvent,
  StockReplenishmentPanel,
} from "@shared-core/data/api-types";
import {
  formatCount,
  formatDateOrDash,
  formatDateTimeOrDash,
} from "@shared-core/domain/formatters";
import {
  CUTOVER_STATE_LABELS,
  RAISES_AS_LABELS,
  expectedArrivalLabel,
  REPLENISHMENT_MODE_LABELS,
  REPLENISHMENT_MODE_SOURCE_LABELS,
  SPEC_STATUS_LABELS,
} from "@shared-core/domain/stock-replenishment";
import { paths } from "@shared-core/routing/routes";
import { useCanManageReplenishment } from "../../app/auth/hooks";
import { Badge } from "../../app/components/primitives/badge";
import { Button } from "../../app/components/primitives/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../app/components/primitives/card";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "../../app/components/primitives/collapsible";
import { ReplenishNowDialog } from "./ReplenishNowDialog";
import { ReplenishmentPolicyDialog } from "./ReplenishmentPolicyDialog";

/**
 * The stock item page's replenishment panel (BrandHub doc 09): how the item is
 * topped up, who is told, what a reprint raises as, any open proposal, and
 * stock of older artwork (batches not printed from the current revision).
 * One composed read; Edit and Replenish now write to the owning module.
 * `onOlderArtworkBatchIds` hands the view's older-artwork batch ids to the
 * page, so its batches table can mark them without deriving anything itself.
 */
export function ReplenishmentPanel({
  stockItemId,
  onOlderArtworkBatchIds,
}: {
  stockItemId: string;
  onOlderArtworkBatchIds?: (batchIds: string[]) => void;
}) {
  const dataAdapter = useDataAdapter();
  const canManage = useCanManageReplenishment();
  const [panel, setPanel] = useState<StockReplenishmentPanel | null>(null);
  const [status, setStatus] = useState<"loading" | "ready" | "error">(
    "loading",
  );
  const [isEditing, setIsEditing] = useState(false);
  const [isReplenishing, setIsReplenishing] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const result = await dataAdapter.getStockReplenishmentPanel(stockItemId);
      setPanel(result);
      setStatus(result ? "ready" : "error");
      onOlderArtworkBatchIds?.(
        result?.older_artwork_batches.map((b) => b.batch_id) ?? [],
      );
    } catch (error) {
      console.error("stock.replenishment.panel_load_failed", {
        status: error instanceof ApiError ? error.status : undefined,
      });
      setStatus("error");
    }
  }, [dataAdapter, stockItemId, onOlderArtworkBatchIds]);

  useEffect(() => {
    setStatus("loading");
    void load();
  }, [load]);

  if (status === "loading") {
    return (
      <Card aria-busy="true">
        <CardHeader>
          <CardTitle>Replenishment</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            Loading replenishment…
          </p>
        </CardContent>
      </Card>
    );
  }

  if (status === "error" || panel == null) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Replenishment</CardTitle>
        </CardHeader>
        <CardContent>
          <div
            role="alert"
            className="flex items-center gap-2 text-sm text-muted-foreground"
          >
            <AlertTriangle className="size-4 shrink-0" />
            Couldn't load replenishment for this item. The rest of the page
            still works.
          </div>
        </CardContent>
      </Card>
    );
  }

  const { policy } = panel;
  const isBlocked = panel.raise_blocked_reason != null;

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-4 flex-wrap">
        <div className="space-y-1">
          <CardTitle>Replenishment</CardTitle>
          {policy && (
            <div className="flex flex-wrap gap-1">
              <Badge
                variant={policy.mode === "automatic" ? "success" : "neutral"}
              >
                {REPLENISHMENT_MODE_LABELS[policy.mode]}
              </Badge>
              {policy.is_live ? (
                <Badge variant="success">Live</Badge>
              ) : (
                <Badge variant="warning">Shadow only</Badge>
              )}
            </div>
          )}
        </div>
        {canManage && (
          <div className="flex flex-wrap items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => setIsEditing(true)}
            >
              <Pencil className="size-4" />
              {policy ? "Edit" : "Set up policy"}
            </Button>
            {policy && (
              <Button
                size="sm"
                onClick={() => setIsReplenishing(true)}
                disabled={isBlocked || panel.open_proposal != null}
                aria-describedby={
                  isBlocked ? "replenish-now-blocked" : undefined
                }
              >
                <RefreshCw className="size-4" />
                Replenish now
              </Button>
            )}
          </div>
        )}
      </CardHeader>
      <CardContent className="space-y-4">
        {notice && (
          <div
            role="status"
            className="text-sm p-3 rounded-lg border border-green-300 bg-green-50 text-green-800 dark:bg-green-900/20 dark:border-green-800 dark:text-green-200"
          >
            {notice}
          </div>
        )}

        {panel.open_proposal && (
          <div className="flex items-center justify-between gap-3 flex-wrap text-sm p-3 rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-800 text-amber-900 dark:text-amber-200">
            <span>
              A replenishment proposal for{" "}
              {formatCount(panel.open_proposal.proposed_quantity)} is waiting,
              raised {formatDateTimeOrDash(panel.open_proposal.created_at)}.
            </span>
            <Link
              to={paths.stockReplenishmentProposal({
                proposalId: panel.open_proposal.id,
              })}
              className="font-medium underline"
            >
              Open proposal
            </Link>
          </div>
        )}

        {isBlocked && canManage && policy && (
          <p
            id="replenish-now-blocked"
            className="text-sm text-muted-foreground"
          >
            {panel.raise_blocked_reason}
          </p>
        )}

        {policy == null ? (
          <p className="text-sm text-muted-foreground">
            No replenishment policy yet, so nothing tops this item up. The
            cutover seeds one for each item it imports
            {canManage ? ", or set one up here." : "."}
          </p>
        ) : (
          <dl className="grid gap-x-6 gap-y-3 sm:grid-cols-2 lg:grid-cols-3 text-sm">
            <Detail
              term="Re-order at / quantity"
              value={
                panel.reorder_level == null
                  ? "No re-order level"
                  : `${formatCount(panel.reorder_level)} / ${
                      panel.reorder_quantity == null
                        ? "–"
                        : formatCount(panel.reorder_quantity)
                    }`
              }
            />
            <Detail
              term="Needed within"
              value={
                policy.needed_within_days == null
                  ? "No limit"
                  : `${policy.needed_within_days} working day${policy.needed_within_days === 1 ? "" : "s"}`
              }
            />
            <OnOrderDetail onOrder={panel.on_order} />
            <Detail
              term="Mode"
              value={`${REPLENISHMENT_MODE_LABELS[policy.mode]} (${
                REPLENISHMENT_MODE_SOURCE_LABELS[policy.mode_source]
              })`}
            />
            <Detail
              term="Spec"
              value={
                policy.is_tharstern_make
                  ? `${SPEC_STATUS_LABELS[policy.spec_status]} · Tharstern (make)`
                  : SPEC_STATUS_LABELS[policy.spec_status]
              }
              tone={
                policy.spec_status === "spec_needed" ? "warning" : undefined
              }
            />
            <ArtworkDetail panel={panel} />
            <Detail
              term="Deliver into"
              value={
                panel.delivery_warehouse_name == null
                  ? policy.delivery_warehouse_id
                  : panel.delivery_bin
                    ? `${panel.delivery_warehouse_name} · ${panel.delivery_bin}`
                    : `${panel.delivery_warehouse_name} · no bin recorded`
              }
            />
            <Detail
              term="Preferred route"
              value={
                panel.preferred_route_label ??
                policy.preferred_route_code ??
                "Any route (sourcing picks)"
              }
            />
            <Detail
              term="Stock-line owner"
              value={panel.stock_line_owner_name ?? "Nobody told yet"}
              tone={panel.stock_line_owner_name ? undefined : "muted"}
            />
            <Detail
              term="Raises as"
              value={
                panel.raises_as
                  ? RAISES_AS_LABELS[panel.raises_as]
                  : "The owner has no billing account yet"
              }
              tone={panel.raises_as ? undefined : "warning"}
            />
            <Detail
              term="Owner"
              value={`${panel.owner.name ?? "Owner name unavailable"} · ${
                panel.owner.cutover_state
                  ? CUTOVER_STATE_LABELS[panel.owner.cutover_state]
                  : "Not in the cutover"
              }`}
            />
          </dl>
        )}

        {panel.events.length > 0 && <ChangeLog events={panel.events} />}
      </CardContent>

      {isEditing && (
        <ReplenishmentPolicyDialog
          panel={panel}
          onClose={() => setIsEditing(false)}
          onSaved={() => {
            setIsEditing(false);
            setNotice("Replenishment policy saved.");
            void load();
          }}
        />
      )}
      {isReplenishing && policy && (
        <ReplenishNowDialog
          panel={panel}
          onClose={() => setIsReplenishing(false)}
          onRaised={(quantity) => {
            setIsReplenishing(false);
            setNotice(`Reprint of ${formatCount(quantity)} raised.`);
            void load();
          }}
        />
      )}
    </Card>
  );
}

/** What is on order into the item, whoever raised it: the quantity, the lines and the next arrival. */
function OnOrderDetail({
  onOrder,
}: {
  onOrder: StockReplenishmentPanel["on_order"];
}) {
  if (onOrder.quantity === 0) {
    return <Detail term="On order" value="Nothing" tone="muted" />;
  }
  return (
    <div>
      <dt className="text-muted-foreground">On order</dt>
      <dd className="font-medium">
        {formatCount(onOrder.quantity)}
        <span className="block font-normal text-muted-foreground">
          {onOrder.lines.map((line, index) => (
            <span key={line.line_ref}>
              {index > 0 && ", "}
              <Link
                to={paths.orderDetail({ orderNo: line.order_no })}
                className="underline"
              >
                {line.line_ref}
              </Link>
            </span>
          ))}
          {onOrder.next_arrival_on &&
            ` · next arrival ${expectedArrivalLabel(onOrder.next_arrival_on, onOrder.next_arrival_confidence)} (${onOrder.next_arrival_confidence ?? "unknown"})`}
        </span>
      </dd>
    </div>
  );
}

function ArtworkDetail({ panel }: { panel: StockReplenishmentPanel }) {
  const { artwork } = panel;
  if (artwork == null) {
    return (
      <Detail
        term="Artwork"
        value="No approved revision"
        tone={panel.policy?.spec_status === "mapped" ? "warning" : "muted"}
      />
    );
  }
  const validity = artwork.valid_until
    ? `valid until ${formatDateOrDash(artwork.valid_until)}`
    : "doesn't expire";
  return (
    <div>
      <dt className="text-muted-foreground">Artwork</dt>
      <dd className="font-medium">
        {artwork.revision_label}
        <span className="block font-normal text-muted-foreground">
          {validity}
        </span>
        {(artwork.is_expired || artwork.is_inside_margin) && (
          <span className="mt-1 flex items-center gap-1 text-amber-700 dark:text-amber-300 font-normal">
            <AlertTriangle className="size-3.5 shrink-0" />
            {artwork.is_expired
              ? "Expired: a reprint waits for a new revision."
              : "Expires inside the margin: a reprint waits for a person."}
          </span>
        )}
        {panel.older_artwork_batches.length > 0 && (
          <span className="mt-1 block font-normal text-muted-foreground">
            Older artwork in stock, sent first:{" "}
            {panel.older_artwork_batches
              .map(
                (b) =>
                  `batch ${b.batch_no} (${b.artwork_revision_id ?? "revision not recorded"}, ${formatCount(b.on_hand)}${b.valid_until ? `, valid until ${formatDateOrDash(b.valid_until)}` : ""})`,
              )
              .join("; ")}
          </span>
        )}
      </dd>
    </div>
  );
}

const EVENT_FIELD_LABELS: Record<string, string> = {
  mode: "Mode",
  mode_source: "Mode source",
  spec_status: "Spec",
  delivery_warehouse_id: "Deliver into",
  repeat_of_order_line_ref: "Repeat of",
  preferred_route_code: "Preferred route",
  stock_line_owner_user_id: "Stock-line owner",
  needed_within_days: "Needed within (working days)",
  is_tharstern_make: "Tharstern (make)",
  is_live: "Live (One reorders the stock item)",
};

/** A policy event fact: `{from, to}` for a change, `{to}` for a creation. */
function factChange(fact: unknown): { from?: unknown; to?: unknown } {
  return typeof fact === "object" && fact !== null ? fact : { to: fact };
}

/** Enum fields read as words; ids (warehouse, user, route) show as recorded. */
const ENUM_FIELDS = new Set(["mode", "mode_source", "spec_status"]);

function eventValue(field: string, value: unknown): string {
  if (value == null || value === "") return "none";
  if (typeof value === "boolean") return value ? "yes" : "no";
  return ENUM_FIELDS.has(field)
    ? String(value).replace(/_/g, " ")
    : String(value);
}

function ChangeLog({ events }: { events: ReplenishmentPolicyEvent[] }) {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <CollapsibleTrigger className="flex items-center gap-1 text-sm font-medium text-muted-foreground hover:text-foreground">
        {isOpen ? (
          <ChevronDown className="size-4" />
        ) : (
          <ChevronRight className="size-4" />
        )}
        Change log ({events.length})
      </CollapsibleTrigger>
      <CollapsibleContent>
        <ul className="mt-3 space-y-3 text-sm">
          {events.map((event, index) => (
            <li
              key={`${event.at}-${index}`}
              className="border-l-2 border-border pl-3"
            >
              <div className="text-muted-foreground">
                {formatDateTimeOrDash(event.at)} ·{" "}
                {event.actor_name ?? "System (cutover seeding or the sweep)"} ·{" "}
                {event.kind === "created" ? "Created" : "Changed"}
              </div>
              <ul className="mt-1">
                {Object.entries(event.facts).map(([field, fact]) => {
                  const change = factChange(fact);
                  return (
                    <li key={field}>
                      {EVENT_FIELD_LABELS[field] ?? field}:{" "}
                      {"from" in change && (
                        <>
                          <span className="text-muted-foreground line-through">
                            {eventValue(field, change.from)}
                          </span>{" "}
                          →{" "}
                        </>
                      )}
                      {eventValue(field, change.to)}
                    </li>
                  );
                })}
              </ul>
            </li>
          ))}
        </ul>
      </CollapsibleContent>
    </Collapsible>
  );
}

function Detail({
  term,
  value,
  tone,
}: {
  term: string;
  value: string;
  tone?: "warning" | "muted";
}) {
  return (
    <div>
      <dt className="text-muted-foreground">{term}</dt>
      <dd
        className={
          tone === "warning"
            ? "font-medium text-amber-700 dark:text-amber-300"
            : tone === "muted"
              ? "text-muted-foreground"
              : "font-medium"
        }
      >
        {value}
      </dd>
    </div>
  );
}
